import { useEffect } from "react"

import { atom, selector, selectorFamily, useRecoilState } from "recoil"

import type { UtilityRateDto } from "~/api/dto/proposal-utility.dto"
import useFetchUtilityOptions from "~/api/hooks/useFetchUtilityOptions"
import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import type { UtilityCompany, UtilityRate } from "~/graphql/client"

export const DEFAULT_UTILITY_RATE: UtilityRate = {
  id: ``,
  createdAt: ``,
  updatedAt: ``,

  activatedAt: null,
  billingPeriod: ``,
  chargePeriod: null,
  chargeTypes: null,
  closedDate: null,
  currency: ``,
  customerClass: null,
  customerCount: 0,
  customerCountSource: null,
  customerLikelihood: null,
  effectiveDate: ``,
  effectiveOnRule: null,
  endDate: null,
  hasContractedRates: false,
  hasNetMetering: true,
  hasRateApplicability: false,
  hasTariffApplicability: false,
  hasTieredRates: false,
  hasTimeOfUseRates: false,
  isSolarPvEligible: false,
  isNEM3: false,
  isActive: false,
  lseCode: ``,
  lseId: 0,
  lseName: ``,
  masterTariffId: 0,
  maxMonthlyConsumption: null,
  maxMonthlyDemand: null,
  minMonthlyConsumption: null,
  minMonthlyDemand: null,
  priorTariffId: null,
  privacy: null,
  serviceType: null,
  tariffBookName: null,
  tariffCode: ``,
  tariffId: 0,
  tariffName: ``,
  tariffType: null,
  territoryId: null,
  timeZone: ``,
  utilityCompanyId: ``,
  utilityRateZipCodeLikelihoods: [],
  utilityCompany: null,
}

export const DEFAULT_UTILITY_COMPANY: UtilityCompany = {
  id: ``,
  createdAt: ``,
  updatedAt: ``,

  activatedAt: null,
  billingPeriodRepresentation: ``,
  code: ``,
  commercialCustomers: null,
  commercialRevenues: null,
  commercialSales: null,
  commercialServiceTypes: ``,
  industrialCustomers: null,
  industrialRevenues: null,
  industrialSales: null,
  industrialServiceTypes: null,
  lseCode: ``,
  lseId: 0,
  name: ``,
  offeringType: ``,
  ownership: ``,
  ptcTduId: null,
  residentialCustomers: null,
  residentialRevenues: null,
  residentialSales: null,
  residentialServiceTypes: ``,
  serviceTypes: ``,
  totalCustomers: null,
  totalRevenues: null,
  totalSales: null,
  transportationCustomers: null,
  transportationRevenues: null,
  transportationSales: null,
  transportationServiceTypes: null,
  utilityRates: null,
  websiteHome: ``,
}

export const allUtilityRatesState = atom<UtilityRate[]>({
  key: `allUtilityRates`,
  default: [],
})

export const allUtilityCompaniesState = atom<
  Omit<UtilityCompany, `utilityRates`>[]
>({
  key: `allUtilityCompanies`,
  default: [],
})

export const selectedBeforeSolarCompanyNameState = atom<string[]>({
  key: `selectedBeforeSolarCompanyName`,
  default: [],
})

export const selectedBeforeSolarRatePlanState = atom<UtilityRate | null>({
  key: `selectedBeforeSolarRatePlan`,
  default: null,
})

export const selectedAfterSolarCompanyNameState = atom<string[]>({
  key: `selectedAfterSolarCompanyName`,
  default: [],
})

export const selectedAfterSolarRatePlanState = atom<UtilityRate | null>({
  key: `selectedAfterSolarRatePlan`,
  default: null,
})

export const beforeSolarCompanyNameOptionsState = selector<string[]>({
  key: `beforeSolarCompanyNameOptions`,
  get: ({ get }) => {
    const rates = get(allUtilityRatesState)

    return get(allUtilityCompaniesState)
      .filter(({ id }) => rates.some((rate) => rate.utilityCompanyId === id))
      .map((company) => company.name)
  },
})

export const filteredUtilityRatesState = selectorFamily<
  UtilityRate[],
  string | null
>({
  key: `filteredUtilityRates`,
  get:
    (utilityCompanyName) =>
    ({ get }) => {
      const rates = get(allUtilityRatesState).filter((utilityRate) => {
        const { effectiveDate: rawEffectiveDate, endDate: rawEndDate } =
          utilityRate

        const effectiveDate = new Date(rawEffectiveDate ?? ``)
        const endDate = new Date(rawEndDate ?? ``)
        const today = new Date()

        const isAfterEffectiveStartDate = effectiveDate < today
        const endIsLaterThanTodayOrNull = rawEndDate === null || today < endDate

        // mimic's the "openOn" param from the genability tariff's endpoint
        const isWithinEffectiveDateRange =
          isAfterEffectiveStartDate && endIsLaterThanTodayOrNull

        return (
          utilityRate.isActive &&
          isWithinEffectiveDateRange &&
          utilityRate.customerClass?.includes(`RESIDENTIAL`) &&
          [`DEFAULT`, `ALTERNATIVE`].includes(utilityRate.tariffType ?? ``) &&
          utilityRate.lseName === utilityCompanyName &&
          !utilityRate.tariffName.toLowerCase().endsWith(`(closed)`)
        )
      })

      return [...new Set(rates)]
    },
})

function removeObjectDuplicatesById<T extends { id: string }>(array: T[]): T[] {
  return array.filter(
    (thing, index, self) => index === self.findIndex((t) => t.id === thing.id),
  )
}

const CA_UTIL_COMPANIES_TO_FILTER_BY_NEM3: string[] = [
  `Pacific Gas & Electric Co`,
  `San Diego Gas & Electric Co`,
  `Southern California Edison Co`,
]

export const afterSolarFilteredUtilityRatesState = selectorFamily<
  UtilityRate[],
  { utilityCompanyName: string | null; state: string }
>({
  key: `afterSolarFilteredUtilityRates`,
  get:
    (params) =>
    ({ get }) => {
      let rates = get(filteredUtilityRatesState(params.utilityCompanyName))

      // if there is a single rate that is "solar", filter out all non-solar rates
      const containsSolarPlan = rates.some((rate) => rate.isSolarPvEligible)
      if (containsSolarPlan) {
        rates = rates.filter((rate) => rate.isSolarPvEligible)
      }

      // single rate that is isNEM3, filter out all non-NEM3 rates
      const containsNEM3Plan = rates.some((rate) => rate.isNEM3)
      if (containsNEM3Plan) {
        rates = rates.filter((rate) => rate.isNEM3)
      }

      return [...new Set(rates)]
    },
})

export const afterSolarCompanyNameOptionsState = selector<string[]>({
  key: `afterSolarCompanyNameOptions`,
  get: ({ get }) => {
    const rates = get(allUtilityRatesState)
    // const { state } = get(siteLocalState)

    return get(allUtilityCompaniesState)
      .filter(({ id }) => rates.some((rate) => rate.utilityCompanyId === id))
      .map((company) => company.name)
  },
})

// The REST options endpoint serializes the full utility rate row; the DTO
// types the subset the dropdowns rely on. customerLikelihood is flattened
// from the site-zip likelihood entry so default-rate sorting keeps working.
const toUtilityRate = (dto: UtilityRateDto): UtilityRate =>
  ({
    ...dto,
    customerLikelihood:
      dto.utilityRateZipCodeLikelihoods?.[0]?.likelihood ?? null,
    utilityRateZipCodeLikelihoods: dto.utilityRateZipCodeLikelihoods ?? [],
    rates: [],
    utilityCompany: null,
  }) as unknown as UtilityRate

export const useFetchUtilities = (
  proposalId: string,
): {
  companies: Omit<UtilityCompany, `utilityRates`>[]
  rates: UtilityRate[]
} => {
  const [allUtilityRates, setAllUtilityRates] =
    useRecoilState(allUtilityRatesState)
  const [allUtilityCompanies, setAllUtilityCompanies] = useRecoilState(
    allUtilityCompaniesState,
  )
  const onError = useDispatchError()
  const { data, error } = useFetchUtilityOptions(proposalId)

  useEffect(() => {
    if (!data) {
      return
    }

    setAllUtilityCompanies(
      data.companies as unknown as Omit<UtilityCompany, `utilityRates`>[],
    )
    setAllUtilityRates(removeObjectDuplicatesById(data.rates.map(toUtilityRate)))
  }, [data, setAllUtilityCompanies, setAllUtilityRates])

  useEffect(() => {
    if (error) {
      onError(error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  return { companies: allUtilityCompanies, rates: allUtilityRates }
}
